import { Insurance, InsuranceStatus } from '@centecomm/portal-service';
import { InsuranceActions, InsuranceActionTypes } from './actions';
import { InsuranceState } from './Types/InsuranceState';

export const initialState: InsuranceState = {
    insurances: {},
    documents: {},
    notes: {},
    pendingDocuments: {},
    errors: [],
};

export function insuranceReducer(state: InsuranceState = initialState, action: InsuranceActionTypes): InsuranceState {
    switch (action.type) {
        case InsuranceActions.ADD_OCCUPANT_INSURANCES:
            return {
                ...state,
                insurances: {
                    ...state.insurances,
                    [action.payload.occupantId]: action.payload.insurances.reduce(
                        (agg: Record<number, Insurance>, curr: Insurance) => ({
                            ...agg,
                            [curr.id]: curr,
                        }),
                        {},
                    ),
                },
            };
        case InsuranceActions.EDIT_COVERAGE_DATES:
            return {
                ...state,
                insurances: {
                    ...state.insurances,
                    [action.payload.occupantId]: {
                        ...state.insurances[action.payload.occupantId],
                        [action.payload.insuranceId]: {
                            ...state.insurances[action.payload.occupantId][action.payload.insuranceId],
                            coverageStart: action.payload.coverageStart,
                            coverageEnd: action.payload.coverageEnd,
                            status: InsuranceStatus.NotSubmitted,
                        },
                    },
                },
            };
        case InsuranceActions.EDIT_INSURANCE_STATUS:
            return {
                ...state,
                insurances: {
                    ...state.insurances,
                    [action.payload.occupantId]: {
                        ...state.insurances[action.payload.occupantId],
                        [action.payload.insuranceId]: {
                            ...state.insurances[action.payload.occupantId][action.payload.insuranceId],
                            status: action.payload.status,
                        },
                    },
                },
            };
        case InsuranceActions.ADD_INSURANCE_DOCUMENTS:
            return {
                ...state,
                documents: {
                    ...state.documents,
                    [action.payload.insuranceId]: action.payload.documents,
                },
            };
        case InsuranceActions.REMOVE_INSURANCE_DOCUMENT: {
            const docs = state.documents[action.payload.insuranceId] || [];
            const documentIndex = docs.findIndex(({ name }) => name === action.payload.documentId);

            if (documentIndex === -1) return { ...state };

            return {
                ...state,
                documents: {
                    ...state.documents,
                    [action.payload.insuranceId]: [...docs.slice(0, documentIndex), ...docs.slice(documentIndex + 1)],
                },
            };
        }
        case InsuranceActions.ADD_INSURANCE_NOTES:
            return {
                ...state,
                notes: {
                    ...state.notes,
                    [action.payload.insuranceId]: action.payload.notes,
                },
            };
        case InsuranceActions.ADD_PENDING_DOCUMENTS:
            return {
                ...state,
                pendingDocuments: {
                    ...state.pendingDocuments,
                    [action.payload.insuranceId]: {
                        occupantId: action.payload.occupantId,
                        files: action.payload.documents,
                    },
                },
            };
        case InsuranceActions.ADD_PENDING_DOCUMENT: {
            const apdExistingInsurance = state.pendingDocuments[action.payload.insuranceId];

            const apdExistingDocs = apdExistingInsurance ? apdExistingInsurance[action.payload.occupantId] : [];

            return {
                ...state,
                pendingDocuments: {
                    ...state.pendingDocuments,
                    [action.payload.insuranceId]: {
                        occupantId: action.payload.occupantId,
                        files: [...apdExistingDocs, action.payload.document],
                    },
                },
            };
        }
        case InsuranceActions.REMOVE_PENDING_DOCUMENT: {
            const rpdExistingInsurance = state.pendingDocuments[action.payload.insuranceId];
            const rpdExistingDocs = rpdExistingInsurance?.files || [];
            const rpdDocIndex = rpdExistingDocs.findIndex((file: File) => file.name === action.payload.document.name);

            if (rpdDocIndex === -1 || rpdExistingDocs.length < 1)
                return {
                    ...state,
                };

            return {
                ...state,
                pendingDocuments: {
                    ...state.pendingDocuments,
                    [action.payload.insuranceId]: {
                        ...rpdExistingInsurance,
                        files: [...rpdExistingDocs.slice(0, rpdDocIndex), ...rpdExistingDocs.slice(rpdDocIndex + 1)],
                    },
                },
            };
        }
        case InsuranceActions.REMOVE_PENDING_DOCUMENTS: {
            const { [action.payload.insuranceId]: rpdToRemove, ...rest } = state.pendingDocuments;

            return {
                ...state,
                pendingDocuments: {
                    ...rest,
                },
            };
        }
        default:
            return state;
    }
}
